<template>
    <eSesjaModal size="large" class="two-factor-admin">
        <eSesjaModalHeader @close="close">
            {{ $t('com.file.contacts.two-factor-title') }}
            <span class="two-factor-admin__person">{{ personName }}</span>
        </eSesjaModalHeader>
        <eSesjaModalBody :aria-busy="busy">
            <WarningBox v-if="errorKey" role="alert">{{ $t(errorKey) }}</WarningBox>
            <p v-if="busy" role="status">{{ $t('com.file.contacts.two-factor-loading') }}</p>
            <p v-if="successKey" class="two-factor-admin__success" role="status">{{ $t(successKey) }}</p>
            <template v-if="details">
                <eSesjaTab>
                    <nav ref="tabs" class="two-factor-admin__tabs" role="tablist" :aria-label="$t('com.file.contacts.two-factor-title')">
                        <button v-for="(tab, index) in tabs" :key="tab.name" :id="`${tabId(tab.name)}-tab`"
                            type="button" role="tab" :aria-controls="tabId(tab.name)" :aria-selected="activeTab === tab.name"
                            :tabindex="activeTab === tab.name ? 0 : -1" :disabled="busy"
                            :class="['two-factor-admin__tab', { 'two-factor-admin__tab--active': activeTab === tab.name }]"
                            @click="selectTab(tab.name)" @keydown="onTabKeydown($event, index)">
                            {{ $t(tab.label) }}
                        </button>
                    </nav>
                    <eSesjaTabContent :id="`two-factor-content-${personId}`">
                        <eSesjaTabPane :tabId="tabId('overview')" :isActive="activeTab === 'overview'" tabindex="0">
                            <CardWrapper class="two-factor-admin__card two-factor-admin__status">
                                <CardBody>
                                    <span :class="['two-factor-admin__badge', { 'two-factor-admin__badge--enabled': details.isEnabled && !details.requiresReenrollment }]">
                                        <span class="two-factor-admin__status-dot" aria-hidden="true"></span>
                                        {{ $t(statusKey) }}
                                    </span>
                                    <p class="two-factor-admin__description">{{ $t(requirementKey) }}</p>
                                </CardBody>
                            </CardWrapper>
                            <div v-if="details.userId" class="two-factor-admin__metrics">
                                <CardWrapper class="two-factor-admin__card">
                                    <CardBody>
                                        <p class="two-factor-admin__metric-label">{{ $t('com.file.contacts.two-factor-modal-codes-left') }}</p>
                                        <strong class="two-factor-admin__metric-number">{{ details.remainingRecoveryCodes }}</strong>
                                    </CardBody>
                                </CardWrapper>
                                <CardWrapper class="two-factor-admin__card">
                                    <CardBody>
                                        <p class="two-factor-admin__metric-label">{{ $t('com.file.contacts.two-factor-modal-last-use') }}</p>
                                        <strong class="two-factor-admin__metric-date">
                                            {{ details.lastRecoveryCodeUsedUtc ? formatDate(details.lastRecoveryCodeUsedUtc) : $t('com.file.contacts.two-factor-modal-no-use') }}
                                        </strong>
                                    </CardBody>
                                </CardWrapper>
                            </div>
                        </eSesjaTabPane>

                        <eSesjaTabPane :tabId="tabId('settings')" :isActive="activeTab === 'settings'" tabindex="0">
                            <CardWrapper class="two-factor-admin__card">
                                <CardBody>
                                    <CardTitle>{{ $t('com.file.contacts.two-factor-modal-requirement') }}</CardTitle>
                                    <CardText>{{ $t(requirementKey) }}</CardText>
                                    <template v-if="details.canManage && !details.requiredByTenant">
                                        <label class="two-factor-admin__checkbox">
                                            <input v-model="required" type="checkbox" :disabled="busy" />
                                            <span>{{ $t('com.file.contacts.two-factor-require-user') }}</span>
                                        </label>
                                        <eSesjaModalButtonSave type="button" :disabled="busy || required === details.requiredByUser" @save="saveRequirement">
                                            {{ $t('com.global.save') }}
                                        </eSesjaModalButtonSave>
                                    </template>
                                </CardBody>
                            </CardWrapper>
                            <CardWrapper v-if="details.canManage && details.isEnabled" class="two-factor-admin__card two-factor-admin__reset">
                                <CardBody>
                                    <CardTitle>{{ $t('com.file.contacts.two-factor-modal-reset-title') }}</CardTitle>
                                    <CardText>{{ $t('com.file.contacts.two-factor-modal-reset-info') }}</CardText>
                                    <eSesjaModalButtonSave v-if="!confirmReset" type="button" :disabled="busy" @save="confirmReset = true">
                                        {{ $t('com.file.contacts.two-factor-reset') }}
                                    </eSesjaModalButtonSave>
                                    <form v-else @submit.prevent="resetConfiguration">
                                        <label :for="`two-factor-reset-reason-${personId}`" class="esesja-modal__wraper__body__label">
                                            {{ $t('com.file.contacts.two-factor-reset-reason') }}
                                        </label>
                                        <textarea :id="`two-factor-reset-reason-${personId}`" v-model="resetReason" rows="3" maxlength="500"
                                            class="esesja-modal__wraper__body__input two-factor-admin__reason" :disabled="busy" required />
                                        <p>{{ $t('com.file.contacts.two-factor-modal-reset-confirmation') }}</p>
                                        <div class="two-factor-admin__buttons">
                                            <eSesjaModalButtonCancel type="button" :disabled="busy" @close="cancelReset">{{ $t('com.global.cancel') }}</eSesjaModalButtonCancel>
                                            <eSesjaModalButtonSave type="submit" :disabled="busy || !validReason">
                                                {{ $t('com.file.contacts.two-factor-reset-confirm') }}
                                            </eSesjaModalButtonSave>
                                        </div>
                                    </form>
                                </CardBody>
                            </CardWrapper>
                        </eSesjaTabPane>

                        <eSesjaTabPane :tabId="tabId('history')" :isActive="activeTab === 'history'" tabindex="0" :aria-busy="eventsBusy">
                            <p v-if="eventsBusy" role="status">{{ $t('com.file.contacts.two-factor-loading') }}</p>
                            <WarningBox v-else-if="eventsError" role="alert">{{ $t('com.file.contacts.two-factor-load-error') }}</WarningBox>
                            <p v-else-if="eventsLoaded && events.items.length === 0" class="two-factor-admin__empty">
                                {{ $t('com.file.contacts.two-factor-no-events') }}
                            </p>
                            <ol v-else-if="!eventsBusy" class="two-factor-admin__events">
                                <li v-for="event in events.items" :key="event.id" class="two-factor-admin__event">
                                    <div class="two-factor-admin__event-heading">
                                        <strong>{{ eventLabel(event.type) }}</strong>
                                        <time :datetime="event.occurredUtc">{{ formatDate(event.occurredUtc) }}</time>
                                    </div>
                                    <p class="two-factor-admin__event-actor">{{ $t('com.file.contacts.two-factor-actor') }}: {{ event.actorName || $t(event.actorUserId ? 'com.file.contacts.two-factor-user' : 'com.file.contacts.two-factor-system') }}</p>
                                    <p v-if="event.reason" class="two-factor-admin__event-reason">{{ event.reason }}</p>
                                    <details v-if="event.ip" class="two-factor-admin__event-details">
                                        <summary>{{ $t('com.file.contacts.two-factor-modal-event-details') }}</summary>
                                        <span v-if="event.ip">IP: {{ event.ip }}</span>
                                    </details>
                                </li>
                            </ol>
                            <Pagination v-if="events.totalCount > events.pageSize" :totalItems="events.totalCount"
                                :currentPage="events.page" :itemsPerPage="events.pageSize" @goToPage="changePage" />
                        </eSesjaTabPane>
                    </eSesjaTabContent>
                </eSesjaTab>
            </template>
        </eSesjaModalBody>
        <eSesjaModalFooter>
            <eSesjaModalButtonCancel type="button" :disabled="busy" @close="close">{{ $t('com.file.contacts.two-factor-close') }}</eSesjaModalButtonCancel>
        </eSesjaModalFooter>
    </eSesjaModal>
</template>

<script>
import ContactService from "@/services/contact.service";
import { twoFactorStatusKey } from "@/helpers/two-factor.helper";
import eSesjaModal from "@/components/BasicControls/Modals/eSesjaModal.vue";
import eSesjaModalHeader from "@/components/BasicControls/Modals/eSesjaModalHeader.vue";
import eSesjaModalBody from "@/components/BasicControls/Modals/eSesjaModalBody.vue";
import eSesjaModalFooter from "@/components/BasicControls/Modals/eSesjaModalFooter.vue";
import eSesjaModalButtonSave from "@/components/BasicControls/Modals/eSesjaModalButtonSave.vue";
import eSesjaModalButtonCancel from "@/components/BasicControls/Modals/eSesjaModalButtonCancel.vue";
import CardWrapper from "@/components/BasicControls/Cards/CardWrapper.vue";
import CardBody from "@/components/BasicControls/Cards/CardBody.vue";
import CardTitle from "@/components/BasicControls/Cards/CardTitle.vue";
import CardText from "@/components/BasicControls/Cards/CardText.vue";
import WarningBox from "@/components/BasicControls/WarningBox.vue";
import Pagination from "@/components/BasicControls/Pagination.vue";
import eSesjaTab from "@/components/BasicControls/Tabs/eSesjaTab.vue";
import eSesjaTabContent from "@/components/BasicControls/Tabs/eSesjaTabContent.vue";
import eSesjaTabPane from "@/components/BasicControls/Tabs/eSesjaTabPane.vue";

export default {
    name: "PersonTwoFactorModal",
    components: {
        eSesjaModal, eSesjaModalHeader, eSesjaModalBody, eSesjaModalFooter, eSesjaModalButtonSave,
        eSesjaModalButtonCancel, CardWrapper, CardBody, CardTitle, CardText, WarningBox, Pagination,
        eSesjaTab, eSesjaTabContent, eSesjaTabPane,
    },
    props: {
        personId: { type: Number, required: true },
        personName: { type: String, default: "" },
    },
    emits: ["close", "changed"],
    data() {
        return {
            details: null,
            required: false,
            busy: false,
            errorKey: null,
            successKey: null,
            activeTab: "overview",
            tabs: [
                { name: "overview", label: "com.file.contacts.two-factor-modal-tab-overview" },
                { name: "settings", label: "com.file.contacts.two-factor-modal-tab-settings" },
                { name: "history", label: "com.file.contacts.two-factor-modal-tab-history" },
            ],
            confirmReset: false,
            resetReason: "",
            eventsBusy: false,
            eventsLoaded: false,
            eventsError: false,
            eventsVersion: 0,
            events: { items: [], totalCount: 0, page: 1, pageSize: 20 },
        };
    },
    computed: {
        statusKey() {
            return twoFactorStatusKey(this.details);
        },
        requirementKey() {
            if (this.details.requiredByTenant) {
                return "com.file.contacts.two-factor-modal-required-by-tenant";
            }
            if (!this.details.canManage) {
                return "com.file.contacts.two-factor-modal-account-unavailable";
            }
            if (this.details.requiresReenrollment) {
                return "com.file.contacts.two-factor-modal-reenrollment";
            }
            return this.details.requiredByUser
                ? "com.file.contacts.two-factor-modal-required-by-user"
                : "com.file.contacts.two-factor-modal-voluntary";
        },
        validReason() {
            return this.resetReason.trim().length >= 1 && this.resetReason.trim().length <= 500;
        },
    },
    async mounted() {
        this.busy = true;
        try {
            await this.loadDetails();
        }
        catch {
            this.errorKey = "com.file.contacts.two-factor-load-error";
        }
        finally {
            this.busy = false;
        }
    },
    methods: {
        tabId(name) {
            return `two-factor-${this.personId}-${name}`;
        },
        async selectTab(name) {
            if (this.busy) {
                return;
            }
            this.activeTab = name;
            if (name !== "settings") {
                this.cancelReset();
            }
            if (name === "history" && !this.eventsLoaded) {
                await this.changePage(1);
            }
        },
        onTabKeydown(event, index) {
            const movements = { ArrowRight: 1, ArrowLeft: -1, Home: -index, End: this.tabs.length - 1 - index };
            if (this.busy || !Object.prototype.hasOwnProperty.call(movements, event.key)) {
                return;
            }
            event.preventDefault();
            const nextIndex = (index + movements[event.key] + this.tabs.length) % this.tabs.length;
            this.selectTab(this.tabs[nextIndex].name);
            this.$refs.tabs.querySelectorAll('[role="tab"]')[nextIndex].focus();
        },
        async loadDetails() {
            const response = await ContactService.getPersonTwoFactor(this.personId);
            this.details = response.data;
            this.required = Boolean(this.details.requiredByUser);
        },
        async loadEvents(page) {
            if (!this.details?.userId) {
                this.eventsLoaded = true;
                return;
            }
            const version = this.eventsVersion;
            const response = await ContactService.getPersonTwoFactorEvents(this.personId, page, 20);
            if (version === this.eventsVersion) {
                this.events = response.data;
                this.eventsLoaded = true;
            }
        },
        async changePage(page) {
            if (this.busy || this.eventsBusy) {
                return;
            }
            this.eventsBusy = true;
            this.eventsError = false;
            try {
                await this.loadEvents(page);
            }
            catch {
                this.eventsError = true;
            }
            finally {
                this.eventsBusy = false;
            }
        },
        async saveRequirement() {
            if (this.busy || !this.details?.canManage || this.details.requiredByTenant) {
                return;
            }
            await this.mutate(() => ContactService.setPersonTwoFactorRequirement(this.personId, this.required));
        },
        async resetConfiguration() {
            if (this.busy || !this.confirmReset || !this.validReason || !this.details?.canManage || !this.details.isEnabled) {
                return;
            }
            await this.mutate(() => ContactService.resetPersonTwoFactor(this.personId, this.resetReason.trim()), true);
        },
        async mutate(request, isReset = false) {
            this.busy = true;
            this.errorKey = null;
            this.successKey = null;
            try {
                const response = await request();
                this.details = response.data;
                this.required = Boolean(this.details.requiredByUser);
                this.cancelReset();
                this.$emit("changed", { personId: this.personId, details: this.details });
                this.eventsVersion += 1;
                this.eventsLoaded = false;
                if (isReset) {
                    this.activeTab = "overview";
                    this.successKey = "com.file.contacts.two-factor-modal-reset-done";
                }
                else {
                    this.successKey = "com.file.contacts.two-factor-modal-saved";
                }
            }
            catch (error) {
                this.errorKey = error.response?.status === 409 && error.response?.data?.code === "two_factor_required_by_tenant"
                    ? "com.file.contacts.two-factor-required-by-tenant"
                    : "com.file.contacts.two-factor-save-error";
                try {
                    await this.loadDetails();
                    this.$emit("changed", { personId: this.personId, details: this.details });
                }
                catch {
                    // Fail closed when the current account policy cannot be refreshed.
                    this.details = null;
                }
            }
            finally {
                this.busy = false;
            }
        },
        cancelReset() {
            this.confirmReset = false;
            this.resetReason = "";
        },
        eventLabel(type) {
            return this.$t(`com.file.contacts.two-factor-modal-event-${Number(type)}`);
        },
        formatDate(value) {
            return value ? new Intl.DateTimeFormat(this.$i18n.locale, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—";
        },
        close() {
            if (!this.busy) {
                this.$emit("close");
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/esesja-modal.scss';

.two-factor-admin {
    :deep(.esesja-modal__wraper) { width: min(800px, calc(100vw - 32px)); max-height: calc(100dvh - 32px); display: flex; flex-direction: column; }
    :deep(.esesja-modal__wraper__header__title) { font-size: 24px; }
    :deep(.esesja-modal__wraper__header), :deep(.esesja-modal__wraper__body__bottom) { flex-shrink: 0; }
    :deep(.esesja-modal__wraper__body) { padding-bottom: 16px; min-height: 0; }
    :deep(.tab-content) { min-height: 280px; padding: 20px 0 0; }
    :deep(.card-title) { font-size: 16px; font-weight: 600; margin-bottom: 10px; }
    :deep(.card-body) { padding: 20px; }
    :deep(.esesja-modal__wraper__body__bottom-button) { width: auto; min-width: 140px; padding: 12px 20px; height: auto; white-space: normal; }
    &__person { display: block; margin-top: 6px; font-size: 16px; font-weight: 400; color: #5c667c; overflow-wrap: anywhere; }
    &__tabs { display: flex; gap: 24px; border-bottom: 1px solid #dce4ee; position: sticky; top: 0; z-index: 1; background: #f7faff; }
    &__tab { border: 0; border-bottom: 3px solid transparent; padding: 14px 0 12px; background: transparent; color: #5c667c; font-size: 15px; font-weight: 600; }
    &__tab--active { border-bottom-color: #d50027; color: #00082c; }
    &__tab:focus-visible { outline: 2px solid #3457b1; outline-offset: 3px; }
    &__tab:disabled { opacity: .6; cursor: wait; }
    &__card { background: #fff; border-color: #dce4ee; border-radius: 8px; margin-bottom: 16px; }
    &__card:hover { box-shadow: none; }
    &__badge { display: inline-flex; align-items: center; gap: 8px; color: #664d03; font-size: 16px; font-weight: 600; }
    &__badge--enabled { color: #196541; }
    &__status-dot { width: 8px; height: 8px; flex-shrink: 0; border-radius: 50%; background: currentColor; }
    &__description { color: #5c667c; font-size: 14px; line-height: 1.6; margin: 10px 0 0; }
    &__metrics { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    &__metric-label { font-size: 14px; color: #5c667c; margin: 0 0 10px; }
    &__metric-number { display: block; font-size: 32px; line-height: 1.2; color: #00082c; }
    &__metric-date { display: block; font-size: 18px; line-height: 1.5; color: #00082c; }
    &__checkbox { display: flex; gap: 10px; align-items: center; margin: 16px 0; }
    &__reset { border-color: #efc8cd; }
    &__reason { display: block; width: 100%; min-height: 90px; resize: vertical; margin: 8px 0 12px; }
    &__buttons { display: flex; flex-wrap: wrap; gap: 10px; }
    &__success { color: #196541; background: #edf7f0; border-radius: 6px; padding: 12px 16px; }
    &__empty { color: #5c667c; padding: 24px 0; }
    &__events { list-style: none; margin: 0; padding: 0; }
    &__event { padding: 16px; margin-bottom: 12px; background: #fff; border: 1px solid #dce4ee; border-radius: 8px; }
    &__event:last-child { margin-bottom: 0; }
    &__event-heading { display: flex; justify-content: space-between; align-items: baseline; gap: 16px; }
    &__event-heading time { color: #5c667c; font-size: 13px; flex-shrink: 0; }
    &__event-actor { color: #5c667c; font-size: 14px; margin: 6px 0; }
    &__event-reason { white-space: pre-wrap; overflow-wrap: anywhere; margin: 8px 0; font-size: 14px; }
    &__event-details { color: #5c667c; font-size: 13px; margin-top: 10px; }
    &__event-details summary { cursor: pointer; width: fit-content; }
    &__event-details span { display: block; margin-top: 6px; }
    @media (max-width: 600px) {
        :deep(.esesja-modal__wraper__header__title) { padding: 20px 16px 0; font-size: 20px; }
        :deep(.esesja-modal__wraper__body) { padding: 0 16px 16px; max-height: 65vh; }
        :deep(.esesja-modal__wraper__body__bottom) { padding: 0 16px 20px; }
        :deep(.card-body) { padding: 16px; }
        &__tabs { gap: 12px; }
        &__tab { font-size: 14px; flex: 1; }
        &__metrics { grid-template-columns: 1fr; gap: 0; }
        &__event-heading { flex-direction: column; gap: 4px; }
    }
}
</style>
